import { useEffect, useRef, useState } from 'react';
import { useParams } from 'wouter';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import { usePlotsStore } from '@/store/plots';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { apiRequest, authorizedFetch } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { computeFigureHash, loadDraft, wasUncleanClose, applyDraftPatch, saveDraft, markActiveSession, clearActiveSession, clearActiveSessionMarkers, clearDraft } from '@/lib/drafts/localDraft';
import { parseTs } from '@/store/time';
import { idbPutDraft, idbGetDraft, idbDeleteDraft, makeDraftKey } from '@/lib/drafts/indexedDbDraft';
import { safeNormalizeInsightsPayload } from '@/lib/insights/insights-utils';
import type { InsightV2Persistent } from '@/store/plots';
import RecoveryDialog from '@/components/editor/RecoveryDialog';
import LeaveEditorConfirm from '@/components/editor/LeaveEditorConfirm';
import Header from '@/components/layout/header';
import LumiChatV2 from '@/components/chat/lumi-chat-v2';
import PlotCanvas from '@/components/plot/plot-canvas';
import PlotEditor from '@/components/plot/plot-editor-v3';
import { ResizablePanels } from '@/components/layout/resizable-panels';
import { PerformanceMonitor } from '@/components/plot/performance-monitor';
import { Button } from '@/components/ui/button';
import { useTour } from '@/components/onboarding/TourManager';

// Dev-only stub runner for end-to-end testing without AI/network
declare global {
  interface Window { __lumiStubRun?: () => void }
}

function mapProjectId(id?: string) {
  if (!id) return id;
  // Map any demo slug → canonical UUID for demo project
  // No more demo project IDs - use real project IDs
  return id;
}

export default function PlotEditorPage() {
  const { projectId: rawProjectId, plotId: draftIdParam } = useParams<{ projectId?: string; plotId?: string }>();
  const projectId = mapProjectId(rawProjectId);
  const { user } = useAuth();
  const bypassAuth = (() => {
    try { const u = new URL(window.location.href); return u.searchParams.get('stub') === '1' || u.searchParams.get('e2e') === '1'; } catch { return false; }
  })();
  const { setCurrentProject } = useProjectsStore();
  const { loadPlots, currentPlot, clearCurrentPlot, setCurrentPlot, setCurrentPlotArtifacts, applyLiveToCurrentPlot, setEditorMode, resetHydration, setLiveDataFingerprint, withSnapshotGuard, hydration, setBaselineFigure: setBaselineFigureInStore } = usePlotsStore();
  const hydrateFigureFromArtifacts = usePlotsStore(s => s.hydrateFigureFromArtifacts);
  const { go } = useNav();
  const { toast } = useToast();
  const [baselineHash, setBaselineHash] = useState<string | null>(null);
  const [baselineFigure, setBaselineFigure] = useState<any>(null);
  const isDirty = !!(hydration as any)?.guard?.isDirty;
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<number>(0);
  const [showRecovery, setShowRecovery] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [canKeepDraft, setCanKeepDraft] = useState(true);
  // Recovery checked key: tracks which (projectId, draftIdParam) pair has been checked for recovery
  const [recoveryCheckedKey, setRecoveryCheckedKey] = useState<string | null>(null);
  const pendingHrefRef = useRef<string | null>(null);
  const migratedRef = useRef<boolean>(false);
  const isFreshRef = useRef<boolean>(false);
  const sessionIdRef = useRef<string>(
    (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function')
      ? (crypto as any).randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
  const hydrationDoneRef = useRef<boolean>(false);
  // Guards to avoid mount-time backfill racing over freshly duplicated plots
  const hasHydratedFromArtifactsRef = useRef<boolean>(false);
  const allowBackfillRef = useRef<boolean>(true);
  const { startTour, isActive, currentStep, onboardingStatus } = useTour();
  const { projects, isLoading: projectsLoading, loadProjects } = useProjectsStore();
  
  // Ensure projects are loaded for onboarding gating
  useEffect(() => {
    if (!projectsLoading && projects.length === 0 && user) {
      loadProjects().catch((err) => {
        console.error('[plot-editor] Failed to load projects', err);
      });
    }
  }, [user, projectsLoading, projects.length, loadProjects]);
  
  // Fresh route handling: if ?fresh=1, clear session markers, reset store, and skip recovery entirely
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const isFresh = url.searchParams.get('fresh') === '1';
      // Use draftIdParam directly instead of parsing pathname
      const plid = draftIdParam;
      if (!projectId || !plid) return;
      if (!isFresh) return;
      clearActiveSessionMarkers(projectId, plid);
      clearCurrentPlot();
      isFreshRef.current = true;
      // Mark recovery as checked for this specific plot
      setRecoveryCheckedKey(`${projectId}:${plid}`);
      hydrationDoneRef.current = true;
      setShowRecovery(false);
      // Clear any local draft for this brand new plot id
      try { clearDraft(projectId, plid); } catch {}
      // Remove fresh param from URL
      url.searchParams.delete('fresh');
      const newUrl = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash;
      history.replaceState(null, '', newUrl);
      // Telemetry
      try { console.info('editor_fresh_cleared'); } catch {}
    } catch {}
  }, [projectId, draftIdParam, clearCurrentPlot]);
  
  // Check URL for tab parameter to set default active panel
  const [defaultActivePanel, setDefaultActivePanel] = useState('chat');
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get('tab');
      if (tab === 'chat') {
        setDefaultActivePanel('chat');
      } else if (tab === 'editor') {
        setDefaultActivePanel('canvas');
      }
    }
  }, []);

  // Belt-and-suspenders: if current plot's figure is blank, hydrate from artifacts
  useEffect(() => {
    try {
      if (!currentPlot) return;
      const data = (currentPlot as any)?.plotlyJson?.data;
      const empty = !Array.isArray(data) || data.length === 0;
      if (empty && allowBackfillRef.current && !hasHydratedFromArtifactsRef.current) {
        hasHydratedFromArtifactsRef.current = true;
        void hydrateFigureFromArtifacts((currentPlot as any).id);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlot?.id, (currentPlot as any)?.plotlyJson?.data?.length]);

  // Load project and plots
  useEffect(() => {
    if (!user) {
      go(routes.home());
      return;
    }

    if (projectId) {
      // Set hydrationDone immediately for unbound mode (empty state) - will be set again after version load if bound
      if (!draftIdParam) {
        hydrationDoneRef.current = true;
      }
      
      // No more demo projects - always fetch from database
      
      // Fetch project details for real projects
      apiRequest('GET', `/api/projects/${projectId}`)
        .then(res => res.json())
        .then(project => {
          if (!project || !project.id) throw new Error('No project');
          setCurrentProject(project);
          loadPlots(projectId);
          // Set hydrationDone after project loads (for empty state tour trigger)
          hydrationDoneRef.current = true;
          // Set editor mode by route binding
          try {
            if (draftIdParam) setEditorMode('bound'); else setEditorMode('unbound');
          } catch {}
          // If unbound, clear any stale current plot and hydration
          try {
            if (!draftIdParam) { 
              clearCurrentPlot(); 
              resetHydration(); 
            }
          } catch {}
          // Restore per-project current plot selection if present - BUT ONLY when bound to a specific plot
          // When unbound (creating new plot), we should NOT restore previous plot to avoid stale insights
          try {
            if (draftIdParam) {
              const prev = (usePlotsStore.getState() as any).getCurrentFor?.(projectId);
              if (prev && (!usePlotsStore.getState().currentPlot || String((usePlotsStore.getState().currentPlot as any).projectId) !== String(projectId))) {
                setCurrentPlot(prev as any);
              }
            }
          } catch {}
          // If landing on a duplicated plot with a seedFrom param, hydrate from localStorage and clear it
          try {
            const qs = new URLSearchParams(window.location.search);
            const seedFrom = qs.get('seedFrom');
            const plotId = window.location.pathname.split('/')[3];
            if (seedFrom && plotId && seedFrom.startsWith('plot:')) {
              const fromId = seedFrom.slice('plot:'.length);
              const raw = localStorage.getItem(`lumiplot:dupseed:${fromId}`);
              if (raw) {
                const fig = JSON.parse(raw);
                setCurrentPlot({ id: plotId, plotlyJson: fig } as any);
                // Disable empty-canvas backfill during duplicate hydration window
                allowBackfillRef.current = false;
                try { localStorage.removeItem(`lumiplot:dupseed:${fromId}`); } catch {}
                try { usePlotsStore.getState().markUserEdited?.(); } catch {}
              }
            }
          } catch {}
          // If version param present, hydrate from filesystem snapshot
          try {
            const qs = new URLSearchParams(window.location.search);
            const versionId = qs.get('version');
            const pathParts = window.location.pathname.split('/');
            const plotId = pathParts[3]; // /plot-editor/:projectId/:plotId
            if (versionId && plotId) {
              Promise.all([
                authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/${versionId}/plotly_figure.json`).then(r => r.json()),
                authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/${versionId}/edited_script.py`).then(r => r.text()).catch(() => ''),
                authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/${versionId}/meta.json`).then(r => r.ok ? r.json() : {})
              ]).then(([fig, code, meta]) => {
                setCurrentPlot({ id: plotId, plotlyJson: fig, pythonCode: code } as any);
                // Establish baseline from version
                try {
                  const h = computeFigureHash(fig);
                  setBaselineHash(h);
                  setBaselineFigure(fig);
                  // Store baseline figure in global store
                  try {
                    setBaselineFigureInStore(plotId, fig);
                  } catch {}
                  const etagKey = `lumiplot:plot:${plotId}:etag`;
                  const updatedKey = `lumiplot:plot:${plotId}:updatedAt`;
                  try { if ((meta as any)?.etag) localStorage.setItem(etagKey, String((meta as any).etag)); } catch {}
                  try { if ((meta as any)?.updatedAt) localStorage.setItem(updatedKey, String((meta as any).updatedAt)); } catch {}
                } catch {}
              }).catch(() => {});
            }
          } catch {}
        })
        .catch(() => {
          // No fallback to demo project - show error
          console.error('Failed to load project');
          go(routes.dashboard());
        });
    }
  }, [projectId, user, setCurrentProject, loadPlots, go, setCurrentPlot]);

  // Clear current plot and insights when entering unbound mode to prevent stale state
  useEffect(() => {
    // If entering unbound mode (creating new plot), aggressively clear all plot state
    if (!draftIdParam) {
      try {
        console.log('[plot-editor] entering unbound mode: resetting current plot + insights');
        // Clear the entire current plot to prevent history hook from using old plot ID
        clearCurrentPlot();
        // Also clear artifacts/insights explicitly
        setCurrentPlotArtifacts({ id: '', projectId: projectId || '', insights: null, artifactSource: 'version' } as any);
        resetHydration();
      } catch (err) {
        try { console.error('[plot-editor] error clearing unbound state', err); } catch {}
      }
      return;
    }
    
    // If bound to a plot, check if insights match the plotId
    const insights = (currentPlot as any)?.insights;
    if (!insights) return;
    
    const insightsPlotId = String(insights.plotId || '');
    const newPlotId = String(draftIdParam || '');
    
    // If insights don't match the new plotId, clear them immediately
    if (insightsPlotId && newPlotId && insightsPlotId !== newPlotId) {
      try {
        console.warn('[plot-editor] clearing mismatched insights on plotId change', { insightsPlotId, newPlotId });
        setCurrentPlotArtifacts({ id: newPlotId, projectId: projectId || '', insights: null, artifactSource: 'version' } as any);
      } catch {}
    }
  }, [draftIdParam, currentPlot?.insights?.plotId, projectId, clearCurrentPlot, resetHydration, setCurrentPlotArtifacts]);

  // Flip editorMode when :plotId changes, and perform hydration for bound state
  useEffect(() => {
    try {
      if (draftIdParam) setEditorMode('bound'); else setEditorMode('unbound');
      if (!draftIdParam) {
        clearCurrentPlot();
        resetHydration();
      }
      // Replay any queued analysis event once bound
      if (draftIdParam) {
        try {
          const d: any = (window as any).__lumiplotQueuedAnalysisEvent || null;
          if (d && d.plotId && d.projectId) {
            (window as any).__lumiplotQueuedAnalysisEvent = null;
            setCurrentPlotArtifacts({ id: String(d.plotId), projectId: String(d.projectId), plotlyJson: d.figure || undefined, statsJson: d.stats || undefined, latestDataFingerprintId: d.dataFingerprintId || undefined, pythonCode: d.pythonCode || undefined, javascriptCode: d.javascriptCode || undefined, artifactSource: 'draft' } as any);
            applyLiveToCurrentPlot();
          }
        } catch {}
        // Ensure any previously stored context fingerprint is mirrored into the visible draft
        try {
          const pid = String(projectId || '');
          const sid = String(draftIdParam || '');
          if (pid && sid) {
            const store = usePlotsStore.getState();
            const fp = store.getLiveDataFingerprint?.(pid, sid) || null;
            const curFp = (usePlotsStore.getState().currentPlot as any)?.latestDataFingerprintId || null;
            if (fp && !curFp) {
              setCurrentPlotArtifacts({ id: sid, projectId: pid, latestDataFingerprintId: fp } as any);
              applyLiveToCurrentPlot();
            }
          }
        } catch {}
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdParam]);

  // Single normalized listener for analysis:plot_built to hydrate artifacts and codes.
  // If unbound during navigation, queue and replay once bound.
  // Gate events to only hydrate the current bound plot to prevent cross-plot state leakage.
  useEffect(() => {
    const handler = (e: any) => {
      try {
        const d = (e?.detail || {}) as any;
        const incomingPlotId = String(d.plotId || '');
        const incomingProjectId = String(d.projectId || '');
        if (!incomingPlotId || !incomingProjectId) return;

        // If we're currently bound to a plot, then only accept events
        // that match both project and plot.
        if (draftIdParam) {
          if (incomingProjectId !== projectId) return;
          if (incomingPlotId !== draftIdParam) return;
        } else {
          // In unbound mode, queue events for replay once bound.
          (window as any).__lumiplotQueuedAnalysisEvent = d;
          return;
        }

        const payload = {
          id: incomingPlotId,
          projectId: incomingProjectId,
          plotlyJson: d.figure || undefined,
          statsJson: d.stats || undefined,
          latestDataFingerprintId: d.dataFingerprintId || undefined,
          pythonCode: d.pythonCode || undefined,
          javascriptCode: d.javascriptCode || undefined,
          artifactSource: 'draft' as const,
        };
        setCurrentPlotArtifacts(payload as any);
        applyLiveToCurrentPlot();
        // Defensive: if fid missing, try to pull from latest version meta.json or drafts meta.json
        (async () => {
          try {
            const sid = incomingPlotId; const pid = incomingProjectId;
            let meta: any = null;
            try {
              const r = await authorizedFetch(`/api/ai/v2/plots/${sid}/versions/latest/meta.json`, { allow404: true } as any);
              if ((r as any).ok) meta = await (r as any).json();
            } catch {}
            if (!meta) {
              try {
                const r2 = await authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/meta.json`, { allow404: true } as any);
                if ((r2 as any).ok) meta = await (r2 as any).json();
              } catch {}
            }
            const fid = String((meta as any)?.datasetFileId || (meta as any)?.dataFingerprintId || '') || null;
            if (fid) {
              try { setLiveDataFingerprint(String(pid), String(sid), fid, { source: 'meta' }); } catch {}
              try { setCurrentPlotArtifacts({ id: sid, projectId: pid, latestDataFingerprintId: fid } as any); } catch {}
              applyLiveToCurrentPlot();
            }
            // REMOVED: Fallback that picks first file from project - this was causing wrong data to be shown
            // The fingerprint should come from plot artifacts (plot_artifacts_ready event) or metadata
            // Picking a random file can show data from a different dataset than what was used for the plot
          } catch {}
        })();
      } catch {}
    };
    window.addEventListener('analysis:plot_built' as any, handler as any);
    return () => window.removeEventListener('analysis:plot_built' as any, handler as any);
  }, [projectId, draftIdParam, setCurrentPlotArtifacts, applyLiveToCurrentPlot]);

  // Expose a stub run to simulate a full analysis result without AI or network
  useEffect(() => {
    try {
      const makeStub = () => {
        const parts = (typeof window !== 'undefined' ? window.location.pathname.split('/') : []) as string[];
        const projectId = parts && parts[2] ? String(parts[2]) : 'dev-project';
        const plotId = parts && parts[3] ? String(parts[3]) : 'dev-plot';
        const figure = {
          data: [
            { type: 'bar', x: ['Cs', 'Ec', 'Ka', 'Rg'], y: [0.93, 1.00, 0.99, 0.94], name: 'Series A (n=12)' },
            { type: 'bar', x: ['Cs', 'Ec', 'Ka', 'Rg'], y: [0.91, 0.99, 0.98, 0.93], name: 'Series B (n=8)' }
          ],
          layout: { title: 'Stub Plot', barmode: 'group' },
          config: {}
        } as any;
        const stats = {
          omnibus: { test: 'ANOVA', F: 3.21, p: 0.041 },
          effect_sizes: { eta2: 0.12 },
          n_by_x: { Cs: 20, Ec: 20, Ka: 20, Rg: 20 },
          n_by_hue: { 'Series A': 12, 'Series B': 8 },
          // intentionally omit legacy n_per_group to exercise shim
          species_order: ['Cs','Ec','Ka','Rg'],
          ci_method: 't-distribution, 95% CI',
          // use object-map style with ci95 aliases to exercise normalization
          group_stats: {
            Cs: { n: 1771, mean: 0.9325413788424618, sem: 0.006199256126347343, ci95_low: 0.9203827458623286, ci95_high: 0.944700011822595 },
            Ec: { n: 3544, mean: 0.9999845691481377, sem: 0.002944045094784804, ci95_low: 0.9942123748997846, ci95_high: 1.0057567633964908 },
            Ka: { n: 5316, mean: 0.9883593902101203, sem: 0.0014393986644533115, ci95_low: 0.985537578070362, ci95_high: 0.9911812023498786 },
            Rg: { n: 3544, mean: 0.9337204489788374, sem: 0.004018434183289946, ci95_low: 0.9258417711959256, ci95_high: 0.9415991267617493 }
          },
          stats_display: {
            n_line: 'Cs (n=1771), Ec (n=3544), Ka (n=5316), Rg (n=3544)',
            overall_line: 'ANOVA: F=3.21, p=0.041'
          }
        } as any;
        const event = new CustomEvent('analysis:plot_built' as any, {
          detail: { plotId, projectId, figure, stats }
        } as any);
        window.dispatchEvent(event);
      };
      (window as any).__lumiStubRun = makeStub;
      // Optional auto-run when ?stub=1 present (works in prod builds too)
      const url = new URL(window.location.href);
      if (url.searchParams.get('stub') === '1') {
        setTimeout(() => { try { makeStub(); } catch {} }, 50);
      }
      return () => { try { delete (window as any).__lumiStubRun; } catch {} };
    } catch {}
  }, []);

  // Deterministic hydration for existing plots (baseline snapshot first). Skip when unbound.
  useEffect(() => {
    (async () => {
      try {
        if (!projectId) return;
        if (!draftIdParam) return; // unbound editor - skip hydration
        if (isFreshRef.current) return; // skip fresh
        // Use draftIdParam directly instead of parsing pathname to avoid stale values
        const plotId = draftIdParam;
        if (!plotId) return;
        try { console.info('[plot-editor] starting hydration', { plotId, projectId, draftIdParam }); } catch {}
        const [figRes, metaRes, codeRes, statsRes, insightsRes] = await Promise.all([
          authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/plotly_figure.json`).catch((err) => {
            try { console.warn('[plot-editor] figure fetch failed', { plotId, error: String(err) }); } catch {}
            return { ok: false } as any;
          }),
          authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/meta.json`).catch((err) => {
            try { console.warn('[plot-editor] meta fetch failed', { plotId, error: String(err) }); } catch {}
            return { ok: false } as any;
          }),
          authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/edited_script.py`).catch(() => ({ ok: false } as any)),
          authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/stats.json`).catch(() => ({ ok: false } as any)),
          authorizedFetch(`/api/ai/v2/plots/${plotId}/versions/latest/insights.json`).catch((err) => {
            try { console.warn('[plot-editor] insights fetch failed in Promise.all', { plotId, error: String(err) }); } catch {}
            return { ok: false } as any;
          })
        ]);
        let fig: any = null;
        if (figRes.ok) {
          try {
            fig = await figRes.json();
            // Validate that the figure has data
            if (!fig || !Array.isArray(fig.data) || fig.data.length === 0) {
              try { console.warn('editor_hydrated_from_snapshot: API returned figure with no data', { plotId, figDataLength: fig?.data?.length }); } catch {}
              fig = null; // Treat as failed if no data
            }
          } catch (parseErr) {
            try { console.error('editor_hydrated_from_snapshot: Failed to parse figure JSON', parseErr); } catch {}
            fig = null;
          }
        }
        
        // Process meta and insights even if figure is missing (insights are independent)
        const meta = metaRes.ok ? await metaRes.json() : {};
        const versionId = (meta?.id || meta?.versionId || meta?.version_id || null) as any;
        
        // Process insights regardless of figure availability
        try { console.info('[plot-editor] about to process insights', { plotId, projectId, hasMeta: !!meta, versionId, hasFig: !!fig }); } catch {}
        let insightsJson: any = undefined;
        try {
          // Log insightsRes structure for debugging
          try { 
            console.info('[plot-editor] processing insights fetch result', { 
              plotId, 
              insightsResType: typeof insightsRes,
              insightsResOk: (insightsRes as any)?.ok, 
              insightsResStatus: (insightsRes as any)?.status,
              insightsResKeys: insightsRes ? Object.keys(insightsRes as any) : [],
              insightsRes: insightsRes
            }); 
          } catch {}
          
          if ((insightsRes as any)?.ok) {
            insightsJson = await (insightsRes as any).json();
            try { console.info('[plot-editor] insights.json loaded from version', { plotId, blocksCount: insightsJson?.blocks?.length || 0, schemaVersion: insightsJson?.schemaVersion, schema_version: insightsJson?.schema_version }); } catch {}
          } else {
            // Log the failure reason
            const status = (insightsRes as any)?.status;
            const statusText = (insightsRes as any)?.statusText;
            try { console.warn('[plot-editor] insights.json fetch failed from version', { plotId, status, statusText, insightsResType: typeof insightsRes }); } catch {}
            
            // Fallback: try fetching from draft artifacts if version doesn't exist
            if (projectId && plotId) {
              try {
                try { console.info('[plot-editor] attempting draft insights fallback', { plotId, projectId }); } catch {}
                const draftInsightsRes = await authorizedFetch(`/api/ai/v2/drafts/${projectId}/${plotId}/insights.json`).catch((err) => {
                  try { console.warn('[plot-editor] draft insights fetch caught error', { plotId, error: String(err), errorType: typeof err }); } catch {}
                  return { ok: false } as any;
                });
                if ((draftInsightsRes as any)?.ok) {
                  insightsJson = await (draftInsightsRes as any).json();
                  try { console.info('[plot-editor] insights.json loaded from draft fallback', { plotId, blocksCount: insightsJson?.blocks?.length || 0, schemaVersion: insightsJson?.schemaVersion, schema_version: insightsJson?.schema_version }); } catch {}
                } else {
                  try { console.info('[plot-editor] insights.json not found in draft either', { plotId, draftStatus: (draftInsightsRes as any)?.status, draftOk: (draftInsightsRes as any)?.ok }); } catch {}
                }
              } catch (draftErr) {
                try { console.warn('[plot-editor] draft insights fetch error', { plotId, error: String(draftErr), errorType: typeof draftErr }); } catch {}
              }
            } else {
              try { console.warn('[plot-editor] skipping draft fallback - missing projectId or plotId', { projectId, plotId }); } catch {}
            }
          }
        } catch (parseErr) {
          try { console.error('[plot-editor] insights.json parse error', { plotId, error: String(parseErr), errorType: typeof parseErr }); } catch {}
        }
        
        // Convert backend format (schema_version: "insight.v2") to frontend format (schemaVersion: "InsightV2" with blocks)
        let insightsPersistent: InsightV2Persistent | null = null;
        if (insightsJson) {
          // Already in frontend format
          if (insightsJson.schemaVersion === 'InsightV2' && Array.isArray(insightsJson.blocks)) {
            insightsPersistent = insightsJson as InsightV2Persistent;
            try { console.info('[plot-editor] insights already in InsightV2Persistent format', { plotId, blocksCount: insightsPersistent.blocks.length }); } catch {}
          } 
          // Backend format: convert schema_version: "insight.v2" to InsightV2Persistent
          else if (insightsJson.schema_version === 'insight.v2' || insightsJson.schema_version === 'Insights.v2' || insightsJson.schema_version === 'InsightV2') {
            try {
              const normalized = safeNormalizeInsightsPayload(insightsJson);
              const blocks: InsightV2Persistent['blocks'] = [];
              
              // Add figure caption as summary block
              if (normalized.figure_caption && String(normalized.figure_caption).trim()) {
                blocks.push({
                  id: `caption-${Date.now()}`,
                  role: 'assistant',
                  kind: 'summary',
                  text: String(normalized.figure_caption),
                });
              }
              
              // Add insight cards as note blocks
              if (Array.isArray(normalized.insights) && normalized.insights.length > 0) {
                normalized.insights.forEach((card, idx) => {
                  const title = String(card?.title || '').trim();
                  const body = String(card?.body || '').trim();
                  if (title || body) {
                    blocks.push({
                      id: `insight-${idx}-${Date.now()}`,
                      role: 'assistant',
                      kind: 'note',
                      text: title ? `${title}${body ? `: ${body}` : ''}` : body,
                    });
                  }
                });
              }
              
              // Add next steps as suggestion blocks
              if (Array.isArray(normalized.next_steps) && normalized.next_steps.length > 0) {
                normalized.next_steps.forEach((step, idx) => {
                  const text = String(step || '').trim();
                  if (text) {
                    blocks.push({
                      id: `next-step-${idx}-${Date.now()}`,
                      role: 'assistant',
                      kind: 'suggestion',
                      text,
                    });
                  }
                });
              }
              
              if (blocks.length > 0) {
                insightsPersistent = {
                  schemaVersion: 'InsightV2',
                  projectId: projectId || '',
                  plotId: plotId || null,
                  versionId: versionId || null,
                  generatedAt: new Date().toISOString(),
                  model: (insightsJson as any)?.model || undefined,
                  runId: (insightsJson as any)?.runId || (insightsJson as any)?.run_id || null,
                  blocks,
                };
                try { console.info('[plot-editor] converted backend insights to InsightV2Persistent', { plotId, blocksCount: insightsPersistent.blocks.length }); } catch {}
              } else {
                try { console.warn('[plot-editor] insights normalized but no blocks created', { plotId, normalized }); } catch {}
              }
            } catch (convertErr) {
              try { console.error('[plot-editor] failed to convert insights format', { plotId, error: convertErr, insightsJson }); } catch {}
            }
          } else {
            try { console.warn('[plot-editor] insights in unknown format', { plotId, schemaVersion: insightsJson?.schemaVersion, schema_version: insightsJson?.schema_version, hasBlocks: Array.isArray(insightsJson?.blocks) }); } catch {}
          }
        }
        
        // Store insights immediately if found, even if figure is missing
        if (insightsPersistent) {
          try {
            // CRITICAL: Ensure insights.plotId matches the current plotId to prevent mismatched insights
            const insightsPlotId = insightsPersistent.plotId;
            if (insightsPlotId && insightsPlotId !== plotId) {
              try { console.warn('[plot-editor] insights plotId mismatch, clearing insights', { insightsPlotId, currentPlotId: plotId }); } catch {}
              insightsPersistent = null; // Clear mismatched insights
            } else {
              // Update plotId to ensure it matches current plot
              insightsPersistent.plotId = plotId;
              insightsPersistent.projectId = projectId;
            }
            
            if (insightsPersistent) {
              // Ensure we have a currentPlot container
              if (!usePlotsStore.getState().currentPlot || (usePlotsStore.getState().currentPlot as any).id !== plotId) {
                setCurrentPlot({ id: plotId, projectId, title: (currentPlot as any)?.title || 'Untitled Plot', plotlyJson: { data: [], layout: {} }, pythonCode: null, createdAt: new Date(), creditsConsumed: 0 } as any);
              }
              setCurrentPlotArtifacts({ id: plotId, projectId, insights: insightsPersistent, artifactSource: 'version' } as any);
              try { console.info('[plot-editor] stored insights early (before figure check)', { plotId, blocksCount: insightsPersistent.blocks.length }); } catch {}
            }
          } catch (err) {
            try { console.error('[plot-editor] failed to store insights early', { plotId, error: err }); } catch {}
          }
        }
        
        // Return early if no figure (but insights are already stored above)
        if (!fig) {
          try { toast({ title: 'Latest figure unavailable', description: 'Proceeding without baseline until it is available.', variant: 'default' }); } catch {}
          // Don't proceed with empty figure - return early
          return;
        }
        
        // Optional code artifacts: fetch only when present to avoid 404 noise
        const hasEdited = !!((meta as any)?.files?.scriptEdited);
        const codeTxt = hasEdited && codeRes && (codeRes as any).ok ? await (codeRes as any).text() : '';
        const statsJson = (statsRes as any)?.ok ? await (statsRes as any).json() : undefined;
        const dataFpId = (meta?.datasetFileId || meta?.dataFingerprintId || meta?.data_fingerprint_id || null) as any;
        const h = computeFigureHash(fig);
        setBaselineHash(h);
        setBaselineFigure(fig);
        // Store baseline figure in global store for reset functionality
        try {
          setBaselineFigureInStore(plotId, fig);
          console.log('[plot-editor] Stored baseline figure in store:', { plotId, hasFigure: !!fig });
        } catch (err) {
          console.error('[plot-editor] Failed to store baseline figure:', err);
        }
        const baselineUpdatedAtMs = parseTs((meta as any)?.updatedAt);
        const updatedAtMs = Number.isFinite(baselineUpdatedAtMs) ? baselineUpdatedAtMs : Date.now();
        setBaselineUpdatedAt(updatedAtMs);
        // Ensure we have a currentPlot container before guarded figure apply
        if (!usePlotsStore.getState().currentPlot || (usePlotsStore.getState().currentPlot as any).id !== plotId) {
          setCurrentPlot({ id: plotId, projectId, title: (currentPlot as any)?.title || 'Untitled Plot', plotlyJson: { data: [], layout: {} }, pythonCode: null, createdAt: new Date(), creditsConsumed: 0 } as any);
        }
        // Apply baseline figure via guard (first hydration wins)
        // WIDEN FORCE CONDITIONS: Check for hash mismatch, plotId mismatch, or empty data
        const currentPlotlyJson = usePlotsStore.getState().currentPlot?.plotlyJson;
        const guard = usePlotsStore.getState().hydration?.guard;
        const currentBaselineHash = guard?.baselineHash || null;
        const hasEmptyData = !currentPlotlyJson || !Array.isArray(currentPlotlyJson?.data) || currentPlotlyJson.data.length === 0;
        const hashMismatch = currentBaselineHash && h !== currentBaselineHash;
        // Force if: empty data, hash mismatch, or guard not yet hydrated for this plot
        const shouldForce = hasEmptyData || hashMismatch || !guard?.hydrated;
        
        // Validate that the loaded figure has data before applying
        const figHasData = fig && Array.isArray(fig.data) && fig.data.length > 0;
        if (!figHasData) {
          try { console.warn('editor_hydrated_from_snapshot: loaded figure has no data', { plotId, versionId, figDataLength: fig?.data?.length }); } catch {}
        }
        
        const guardResult = withSnapshotGuard(fig, { hash: h, updatedAt: String(updatedAtMs) }, 'baseline', { force: shouldForce });
        const wasApplied = guardResult === 'applied';
        
        // DIRECT FALLBACK: If guard skipped and we have valid data, set plotlyJson directly
        // This bypasses the guard entirely when it incorrectly skips
        const needsPlotlyJson = (!wasApplied && figHasData) || (hasEmptyData && figHasData);
        
        // Apply non-figure artifacts, and also plotlyJson if needed
        // Use bypassGuard when guard skipped to ensure plotlyJson is set
        try { 
          const artifactsPayload: any = { 
            id: plotId, 
            projectId, 
            // Always set plotlyJson if it's empty and we have valid figure data
            ...(needsPlotlyJson ? { plotlyJson: fig } : {}),
            pythonCode: codeTxt || undefined, 
            latestVersionId: versionId || undefined, 
            latestDataFingerprintId: dataFpId || undefined, 
            ...(statsJson ? { statsJson } : {}), 
            artifactSource: 'version' 
          };
          // Only include insights if they were successfully converted and plotId matches
          if (insightsPersistent) {
            // CRITICAL: Double-check plotId match before storing
            const insightsPlotId = insightsPersistent.plotId;
            if (insightsPlotId && insightsPlotId !== plotId) {
              try { console.warn('[plot-editor] insights plotId mismatch in artifacts, skipping', { insightsPlotId, currentPlotId: plotId }); } catch {}
            } else {
              // Ensure plotId is set correctly
              insightsPersistent.plotId = plotId;
              insightsPersistent.projectId = projectId;
              artifactsPayload.insights = insightsPersistent;
              try { console.info('[plot-editor] storing insights in artifacts', { plotId, blocksCount: insightsPersistent.blocks.length }); } catch {}
            }
          } else {
            try { console.warn('[plot-editor] insights not included - missing or invalid format', { hasInsightsJson: !!insightsJson, schemaVersion: insightsJson?.schemaVersion, schema_version: insightsJson?.schema_version }); } catch {}
          }
          setCurrentPlotArtifacts(artifactsPayload, { 
            bypassGuard: needsPlotlyJson && !wasApplied 
          }); 
        } catch (err) {
          try { console.error('[plot-editor] failed to set artifacts', err); } catch {}
        }
        
        // If plot doesn't exist (all fetches failed), set hydrationDone so tour can start
        if (!figRes.ok && !metaRes.ok) {
          console.log('[plot-editor] Plot not found (all fetches failed), setting hydrationDone for tour');
          hydrationDoneRef.current = true;
        } else {
          hydrationDoneRef.current = true;
        }
        
        try { console.info('editor_hydrated_from_snapshot', { plotId, versionId, guardResult: guardResult, wasApplied, hasEmptyData, shouldForce, figHasData, needsPlotlyJson }); } catch {}
      } catch {}
    })();
  }, [projectId, draftIdParam]); // Add draftIdParam to dependencies so hydration re-runs when route changes

  // Disable legacy baseline probing on fresh/new routes
  useEffect(() => {
    if (isFreshRef.current) return;
  }, [currentPlot?.id]);

  // Phase C migration: move legacy localStorage draft to IndexedDB after baseline is ready
  useEffect(() => {
    (async () => {
      try {
        if (migratedRef.current) return;
        // Use draftIdParam directly instead of parsing pathname
        const pid = projectId;
        const plid = draftIdParam;
        if (!pid || !plid) return;
        if (!baselineFigure) return; // Need baseline to build full figure from patch
        const legacy = loadDraft(pid, plid);
        if (!legacy?.head) { migratedRef.current = true; return; }
        const applied = applyDraftPatch(baselineFigure, legacy.head);
        const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
        const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
        const tenantId = (window as any).__lumiTenantId || null;
        const nsKey = makeDraftKey(tenantId, userId, pid, plid);
        const payload = { projectId: pid, plotId: plid, baselineHash: baselineHash ?? 'EMPTY', updatedAt: Date.now(), figure: applied };
        const put = await idbPutDraft(nsKey, payload);
        if (put.ok) {
          try { clearDraft(pid, plid); } catch {}
          migratedRef.current = true;
        }
      } catch {}
    })();
  }, [projectId, draftIdParam, baselineFigure, baselineHash]);

  // Skip duplicate code fetching here; hook handles it. Ensure no fetch on fresh.
  useEffect(() => { /* no-op: code is handled by use-plotly-code */ }, [projectId, (currentPlot as any)?.id]);

  // Auto-start onboarding tour only for genuinely new users with no plots
  useEffect(() => {
    console.log('[Tour] Auto-start check:', { 
      user: !!user, 
      projectId, 
      hasCurrentPlot: !!currentPlot,
      plotlyJson: !!currentPlot?.plotlyJson,
      hydrationDone: hydrationDoneRef.current,
      isActive,
      currentStep,
      onboardingStatus,
      projectsLoading,
      projectsCount: projects.length
    });
    
    if (!user || !projectId) {
      console.log('[Tour] Skipping: missing user or projectId');
      return;
    }
    
    // Wait for both data sources to be ready
    if (projectsLoading || onboardingStatus === 'loading') {
      console.log('[Tour] Waiting for data:', { projectsLoading, onboardingStatus });
      return;
    }
    
    // Don't restart if TourManager says it's active
    if (isActive) {
      console.log('[Tour] Skipping: tour already active at step', currentStep);
      return;
    }
    
    // Only auto-start if the user is genuinely "new" (server-backed status)
    // Testing override: bypass status check when forced
    const forced = (() => {
      try {
        if (typeof window === 'undefined') return false;
        if (import.meta.env.VITE_LUMI_TOUR_FORCE === '1') return true;
        if (localStorage.getItem('lumi:force-tour') === '1') return true;
        return false;
      } catch {
        return false;
      }
    })();
    
    if (!forced && onboardingStatus !== 'new') {
      console.log('[Tour] Skipping auto-start: onboarding status is', onboardingStatus);
      return;
    }
    
    // Never auto-start if the user already has any plots (unless forced)
    const userHasPlots = projects.some(p => (p.plotsCount ?? 0) > 0);
    if (!forced && userHasPlots) {
      console.log('[Tour] Skipping auto-start: user already has plots');
      return;
    }
    
    // Check if current plot is empty - simplified to just check if plot object exists
    // Don't check data.length === 0, as that causes restarts mid-tour when plot has empty data
    const isEmptyState = !currentPlot || !currentPlot?.plotlyJson;
    
    console.log('[Tour] State check:', { isEmptyState, hydrationDone: hydrationDoneRef.current });
    
    // Auto-start if: empty state and editor is ready
    // For unbound mode (no draftIdParam), start immediately after a short delay
    // For bound mode, wait for hydrationDone OR if plot doesn't exist (404), start anyway
    const shouldStart = isEmptyState && (
      hydrationDoneRef.current || 
      !draftIdParam || 
      (draftIdParam && !currentPlot?.plotlyJson)
    );
    
    if (shouldStart) {
      console.log('[Tour] Conditions met, scheduling tour start for new user with no plots');
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        console.log('[Tour] Auto-starting onboarding for new user with no plots');
        // ✅ Let TourManager decide if it's allowed to start
        startTour('onboarding'); // no `force`
      }, 2000); // Increased delay to ensure DOM is fully ready
      return () => clearTimeout(timer);
    } else {
      console.log('[Tour] Conditions not met:', { isEmptyState, hydrationDone: hydrationDoneRef.current, draftIdParam, hasPlotlyJson: !!currentPlot?.plotlyJson });
    }
  }, [user, projectId, currentPlot?.plotlyJson, startTour, draftIdParam, isActive, currentStep, onboardingStatus, projectsLoading, projects]);

  // Dirty autosave is now coordinated by store hydration guard
  useEffect(() => { /* autosave handled by store via markUserEdited/throttle+debounce */ }, [projectId, currentPlot?.plotlyJson]);

  // Recovery dialog: on mount, if local draft newer than baseline or unclean close, offer resume vs last saved
  // Keyed by (projectId, draftIdParam) to run per-plot, not per-mount
  useEffect(() => {
    // In unbound mode, nothing to recover; reset key and bail.
    if (!draftIdParam) {
      setRecoveryCheckedKey(null);
      return;
    }

    if (isFreshRef.current) return;

    const key = `${projectId}:${draftIdParam}`;
    if (recoveryCheckedKey === key) return;

    let cancelled = false;

    (async () => {
      try {
        const plotId = draftIdParam;
        if (!projectId || !plotId) return;
        const draft = loadDraft(projectId, plotId);
        const unclean = wasUncleanClose(projectId, plotId);
        let newer = draft?.head && (draft.head.updatedAt && draft.head.updatedAt > baselineUpdatedAt);
        // Check IndexedDB sentinel as well
        try {
          const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
          const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
          const tenantId = (window as any).__lumiTenantId || null;
          const nsKey = makeDraftKey(tenantId, userId, projectId, plotId);
          const rec = await idbGetDraft(nsKey);
          if (rec?.header?.updatedAt && rec.header.updatedAt > baselineUpdatedAt) {
            newer = true;
            if (!cancelled) {
              setShowRecovery(true);
              try { console.info('editor_recovery_dialog_shown', { plotId }); } catch {}
            }
          } else if (newer || unclean) {
            if (!cancelled) {
              setShowRecovery(true);
              try { console.info('editor_recovery_dialog_shown', { plotId }); } catch {}
            }
          }
        } catch {
          if ((newer || unclean) && !cancelled) {
            setShowRecovery(true);
          }
        }

        if (!cancelled) {
          setRecoveryCheckedKey(key);
        }
      } catch {}
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, draftIdParam, baselineUpdatedAt, recoveryCheckedKey]);

  // Activate session marker only after hydration and recovery decision made
  // Track current plot via draftIdParam, not pathname parsing
  useEffect(() => {
    // Only mark sessions after recovery has had a chance to run/decide.
    if (!recoveryCheckedKey) return;

    // Only mark an active session in bound mode.
    if (!draftIdParam) return;

    if (!projectId) return;
    if (!hydrationDoneRef.current) return;

    const plotId = draftIdParam;
    markActiveSession(projectId, plotId, sessionIdRef.current);
    const onUnload = () => {
      try { clearActiveSession(projectId, plotId, sessionIdRef.current); } catch {}
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      try { clearActiveSession(projectId, plotId, sessionIdRef.current); } catch {}
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [projectId, draftIdParam, recoveryCheckedKey]);

  // beforeunload guard on unsaved
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Listen for successful save to update baseline, concurrency tokens, and clear draft state
  useEffect(() => {
    const onSaved = (e: { detail?: { figure?: any; etag?: string; updatedAt?: string; plotId?: string } }) => {
      try {
        const { figure, etag, updatedAt, plotId: pid } = e.detail || {} as any;
        if (figure) {
          const h = computeFigureHash(figure);
          setBaselineHash(h);
          setBaselineFigure(figure);
          // Update baseline figure in store after successful save
          const useId = pid || (currentPlot as any)?.id;
          if (useId) {
            try {
              setBaselineFigureInStore(useId, figure);
            } catch {}
          }
          try {
            setBaselineUpdatedAt(Date.now());
            if (useId) {
              const etagKey = `lumiplot:plot:${useId}:etag`;
              const updatedKey = `lumiplot:plot:${useId}:updatedAt`;
              if (etag) localStorage.setItem(etagKey, String(etag));
              if (updatedAt) localStorage.setItem(updatedKey, String(updatedAt));
            }
          } catch {}
          try { usePlotsStore.getState().markServerSaved?.({ figure, updatedAt }); } catch {}
        }
      } catch {}
    };
    window.addEventListener('lumiplot-save-success' as any, onSaved);
    return () => window.removeEventListener('lumiplot-save-success' as any, onSaved);
  }, []);

  // Intercept internal link clicks to guard leaving editor
  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      if (!isDirty) return;
      const target = ev.target as HTMLElement;
      const anchor = target?.closest && (target.closest('a') as HTMLAnchorElement | null);
      if (anchor && anchor.href && anchor.getAttribute('href')?.startsWith('/')) {
        ev.preventDefault();
        pendingHrefRef.current = anchor.getAttribute('href');
        setCanKeepDraft(true);
        setShowLeave(true);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty]);

  // Basic back/forward guard
  useEffect(() => {
    const onPop = (ev: PopStateEvent) => {
      if (!isDirty) return;
      ev.preventDefault?.();
      // try to cancel navigation
      try { history.forward(); } catch {}
      setCanKeepDraft(true);
      setShowLeave(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isDirty]);

  if (!user && !bypassAuth) {
    return null;
  }

  // Handle "Add to Figure" action
  const handleAddToFigure = () => {
    if (!projectId || !currentPlot?.id) return;
    const plotId = (currentPlot as any).id;
    // Always use latest version - no figureJsonOverride, always fetch from saved server data
    const versionId = (currentPlot as any)?.latestVersionId || undefined;
    
    let url = routes.project.figures.new(projectId);
    if (plotId) {
      url += `?plotId=${plotId}`;
    }
    if (versionId) {
      url += (plotId ? '&' : '?') + `plotVersionId=${versionId}`;
    }
    
    go(url);
  };

  // Calculate responsive panel widths based on window size
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const chatWidth = Math.min(340, Math.max(280, Math.floor(windowWidth * 0.18)));
  // Make the editor panel wider by default
  const editorWidth = Math.min(520, Math.max(380, Math.floor(windowWidth * 0.26)));
  const canvasWidth = Math.max(480, windowWidth - chatWidth - editorWidth - 100); // Center canvas gets breathing room
  
  // Debug: log insights being passed to Lumi
  useEffect(() => {
    try {
      console.info('[plot-editor] currentPlot insights state', { 
        hasInsights: !!currentPlot?.insights, 
        blocksCount: currentPlot?.insights?.blocks?.length || 0, 
        plotId: currentPlot?.id,
        insightsPlotId: currentPlot?.insights?.plotId,
        insights: currentPlot?.insights 
      });
    } catch {}
  }, [currentPlot?.insights, currentPlot?.id]);
  
  // Validate insights match current plot before passing to chat
  // In unbound mode, never pass insights (even if store still has them temporarily)
  const validatedInsights = (() => {
    // Never pass insights in unbound mode - ensures chat doesn't seed from old plot
    if (!draftIdParam) {
      return null;
    }
    
    const insights = currentPlot?.insights;
    if (!insights) return null;
    
    const currentPlotId = String(currentPlot?.id || '');
    const insightsPlotId = String(insights.plotId || '');
    
    // Only return insights if they match the current plot
    if (currentPlotId && insightsPlotId && insightsPlotId !== currentPlotId) {
      try { console.warn('[plot-editor] insights plotId mismatch, not passing to chat', { insightsPlotId, currentPlotId }); } catch {}
      return null;
    }
    
    // Additional safety: ensure insights.plotId matches draftIdParam
    if (insightsPlotId && insightsPlotId !== draftIdParam) {
      try { console.warn('[plot-editor] insights plotId does not match draftIdParam, not passing to chat', { insightsPlotId, draftIdParam }); } catch {}
      return null;
    }
    
    return insights;
  })();
  
  // Check if there's a plot with data to show the editor
  const hasPlotData = currentPlot?.plotlyJson && 
    Array.isArray(currentPlot.plotlyJson.data) && 
    currentPlot.plotlyJson.data.length > 0;

  const panels = [
    {
      id: 'chat',
      minWidth: 280,
      maxWidth: 400,
      defaultWidth: chatWidth,
      children: <LumiChatV2 
        initialInsights={validatedInsights} 
        isDirty={isDirty}
        onSave={async () => {
          // Trigger save via the save button click
          const saveButton = document.querySelector('[data-testid="button-save"]') as HTMLButtonElement;
          if (saveButton && !saveButton.disabled) {
            saveButton.click();
            // Wait a bit for save to complete
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }}
      />
    },
    {
      id: 'canvas',
      minWidth: 350,
      maxWidth: 1200,
      defaultWidth: canvasWidth,
      children: (
        draftIdParam ? (
          <PlotCanvas 
            unsaved={isDirty} 
            unsavedLabel={baselineHash ? 'Unsaved changes' : 'New'} 
            onAddToFigure={currentPlot?.id && projectId ? handleAddToFigure : undefined}
            isDirty={isDirty}
            onSave={async () => {
              // Trigger save via the save button click
              const saveButton = document.querySelector('[data-testid="button-save"]') as HTMLButtonElement;
              if (saveButton && !saveButton.disabled) {
                saveButton.click();
                // Wait a bit for save to complete
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <div className="text-center space-y-3">
              <div className="text-muted-foreground">Start by chatting with Lumi or upload data</div>
              <Button
                variant="secondary"
                disabled
                className="bg-background text-transparent hover:bg-background border-transparent shadow-none pointer-events-none focus-visible:outline-none focus-visible:ring-0 disabled:opacity-100"
                onClick={() => {
                  try {
                    const fn = (window as any).__lumiEnsureBoundDraft;
                    if (typeof fn === 'function') { fn(); return; }
                  } catch {}
                  try {
                    const el = document.querySelector('[data-testid="input-chat"]') as HTMLTextAreaElement | null;
                    if (el) el.focus();
                  } catch {}
                }}
              >
                Start a new plot
              </Button>
            </div>
          </div>
        )
      )
    },
    // Only show editor panel when there's plot data
    ...(hasPlotData ? [{
      id: 'editor',
      // Wider adjustable range for editor panel
      minWidth: 360,
      maxWidth: 640,
      defaultWidth: editorWidth,
      children: <PlotEditor />
    }] : [])
  ];

  return (
    <div className="flex flex-col h-screen w-screen" data-testid="plot-editor-page">
      <Header />
      {Boolean((currentPlot as any)?.incompleteLatest) && (
        <div className="bg-amber-50 text-amber-900 border-b border-amber-200">
          <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">Latest version is incomplete.</span>
              <span className="opacity-80">Missing: {Array.isArray((currentPlot as any)?.missingArtifacts) ? (currentPlot as any).missingArtifacts.join(', ') : 'required artifacts'}.</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                try {
                  const id = String((currentPlot as any)?.id || '');
                  if (id) {
                    const fn = usePlotsStore.getState().hydrateFigureFromArtifacts;
                    if (fn) fn(id);
                  }
                } catch {}
              }}>Re-duplicate</Button>
              <Button variant="secondary" size="sm" onClick={() => {
                try {
                  const el = document.querySelector('[data-testid="input-chat"]') as HTMLTextAreaElement | null;
                  el?.focus();
                } catch {}
              }}>Regenerate</Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content with Resizable Panels */}
      <div className="flex-1 overflow-hidden w-full">
        <ResizablePanels 
          panels={panels}
          storageKey="lumiplot-panel-widths"
          flexPanelId="canvas"
          defaultActivePanel={defaultActivePanel}
        />
      </div>
      
      {/* Performance Monitor */}
      <PerformanceMonitor />

      {/* Recovery Dialog */}
      <RecoveryDialog
        open={showRecovery}
        onResume={async () => {
          try {
            // Use draftIdParam from closure to ensure we operate on the correct plot
            const plotId = draftIdParam;
            if (!plotId || !projectId) return;
            const draft = loadDraft(projectId, plotId);
            if (draft?.head) {
              const patched = applyDraftPatch(baselineFigure, draft.head);
              withSnapshotGuard(patched, { hash: computeFigureHash(patched), updatedAt: String(Date.now()) }, 'manual', { force: true });
              try { usePlotsStore.getState().markUserEdited?.(); } catch {}
              try { console.info('editor_resume_draft_applied', { plotId }); } catch {}
            } else {
              // Try IndexedDB payload
              try {
                const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
                const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
                const tenantId = (window as any).__lumiTenantId || null;
                const nsKey = makeDraftKey(tenantId, userId, projectId, plotId);
                const rec = await idbGetDraft(nsKey);
                if (rec?.payload?.figure) {
                  const fig = rec.payload.figure;
                  withSnapshotGuard(fig, { hash: computeFigureHash(fig), updatedAt: String(Date.now()) }, 'manual', { force: true });
                  try { usePlotsStore.getState().markUserEdited?.(); } catch {}
                  try { console.info('editor_resume_draft_applied', { plotId }); } catch {}
                }
              } catch {}
            }
          } catch {}
          setShowRecovery(false);
        }}
        onClose={() => setShowRecovery(false)}
      />

      {/* Leave Editor Confirm */}
      <LeaveEditorConfirm
        open={showLeave}
        canKeepDraft={canKeepDraft}
        onStay={() => {
          setShowLeave(false);
          toast({ title: 'Stayed in editor', description: 'Your draft remains in the editor.' });
        }}
        onDiscard={async () => {
          try {
            // Use draftIdParam from closure to ensure we operate on the correct plot
            const plotId = draftIdParam;
            if (!plotId || !projectId) return;
            clearDraft(projectId, plotId);
            try {
              const authEl: any = document.querySelector('meta[name="lumiplot-user-id"]');
              const userId = authEl?.getAttribute('content') || (window as any).__lumiUserId || null;
              const tenantId = (window as any).__lumiTenantId || null;
              const nsKey = makeDraftKey(tenantId, userId, projectId, plotId);
              await idbDeleteDraft(nsKey);
            } catch {}
          } catch {}
          const href = pendingHrefRef.current; pendingHrefRef.current = null;
          setShowLeave(false);
          if (href) go(href);
          toast({ title: 'Changes discarded', description: 'Unsaved draft was cleared.' });
        }}
        onKeep={() => {
          try {
            // Use draftIdParam from closure to ensure we operate on the correct plot
            const plotId = draftIdParam;
            if (!plotId || !projectId || !baselineHash) return;
            const r = saveDraft({ projectId, plotId, baselineHash, baselineFigure, workingFigure: currentPlot?.plotlyJson, promoteLastGood: true });
            if (!r.ok) {
              setCanKeepDraft(false);
              toast({ title: 'Cannot keep draft', description: 'Local draft too large or write failed.', variant: 'destructive' });
              return;
            }
          } catch {
            setCanKeepDraft(false);
            toast({ title: 'Cannot keep draft', description: 'Local draft write failed.', variant: 'destructive' });
            return;
          }
          const href = pendingHrefRef.current; pendingHrefRef.current = null;
          setShowLeave(false);
          if (href) go(href);
          toast({ title: 'Draft kept', description: 'Your draft was saved locally.' });
        }}
      />
    </div>
  );
}
